/**
 * Hook to intercept external link clicks and open them in default browser
 * Only active when running in Tauri desktop app
 */


import { useEffect } from 'react';
import { createLogger } from '@/lib/logger';
import { downloadFileWithFeedback } from '@/lib/utils/download';
import { isTauri, isExternalUrl, openExternal } from '@/lib/utils/tauri';

const logger = createLogger('hooks/useExternalLinks');

export function useExternalLinks() {
  useEffect(() => {
    // Only run in Tauri
    if (!isTauri()) return;

    const handleClick = async (event: MouseEvent) => {
      // Find the closest anchor element
      const target = event.target as HTMLElement;
      const anchor = target.closest('a');

      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      // Safety net, not the mechanism: nothing in the desktop main process
      // intercepts downloads, so an `<a download>` left to the webview does
      // nothing at all. Downloads should route through `saveBlob` at the call
      // site — this catches markup we don't own (editors, embedded viewers),
      // including anchors those libraries click programmatically.
      //
      // This cannot recurse into itself: the only anchor the app builds lives
      // in `download.ts`, and that branch runs only when `isTauri()` is false,
      // which is exactly when this listener is not attached.
      if (anchor.hasAttribute('download')) {
        logger.warn(
          'Anchor download intercepted; migrate this call site to saveBlob',
          href,
        );
        // No stopPropagation: component-level click handlers on the same anchor
        // still need to run.
        event.preventDefault();
        await downloadFileWithFeedback(
          href,
          anchor.getAttribute('download') || undefined,
        );
        return;
      }

      // A `blob:` anchor without `download` is an open/preview intent. Forcing
      // it into a save dialog would be a different bug, so leave it alone.
      if (href.startsWith('blob:')) {
        return;
      }

      // Check if it's an external URL
      if (isExternalUrl(href)) {
        event.preventDefault();
        event.stopPropagation();
        await openExternal(href);
      }
    };

    // Add click listener to document
    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, []);
}

export default useExternalLinks;
